import { dbDelete, dbGet, dbKeys, dbSet } from '../db/idb';
import { upsertKeyframe } from '../animation/keyframes';
import { RATIO_DIMS } from '../types';
import type {
  AudioTrack,
  Easing,
  Keyframe,
  Layer,
  LayerGroup,
  Project,
  ProjectMeta,
  ScriptChunk,
  Transform,
} from '../types';

/** Legacy localStorage key — used only for one-shot migration to IDB. */
const LEGACY_STORAGE_KEY = 'promo-studio:project';
// Larger debounce coalesces a flurry of edits (slider drags, typing,
// keyframe nudges) into a single IDB write. The user feels lag from
// repeated multi-MB writes more than from a 700ms persistence latency.
const SAVE_DEBOUNCE = 700;
const META_KEY_CURRENT = 'meta:currentProjectId';
const PROJECT_PREFIX = 'project:';
const HISTORY_PREFIX = 'history:';

/** One history entry — the project clone plus a short human-readable
 *  label ("이미지1 · 이동", "Layer1: 색상" …) and a timestamp so the popover
 *  can show the trail. */
export interface HistoryEntry {
  project: Project;
  label: string;
  t: number;
}

// (PersistedHistory was removed — history is now session-only.)

type Listener = (state: State) => void;

export type Tool = 'select' | 'pan' | 'lasso' | 'draw';

export interface State {
  project: Project;
  selectedId: string | null;
  /** Multi-selection set. `selectedId` always points to the most recently
      selected entry (or null). Mirror of selectedIds[selectedIds.length-1]. */
  selectedIds: string[];
  /** Layer being edited inline on the canvas (text only, for now). */
  editingId: string | null;
  playhead: number; // ms
  isPlaying: boolean;
  recordMode: boolean;
  /** Active editing tool. */
  tool: Tool;
  /** Zoom multiplier on top of the auto-fit scale. 1 = fit. */
  zoom: number;
  /** Effective display scale (fit-scale * zoom) — set by the canvas
      after each fit() so the topbar can show the real %. */
  displayScale: number;
  /** Pen color / width for the drawing tool. */
  penColor: string;
  penWidth: number;
  /** 'pen' draws, 'eraser' uses destination-out (or transparent strokes). */
  penMode: 'pen' | 'eraser';
  /** Stroke shape: round (smooth) vs butt (sharp). */
  penShape: 'round' | 'butt';
  /** When ON, inspector edits and canvas drags auto-record keyframes at the
      current playhead. Session-only (not persisted). */
  autoKeyframe: boolean;
  /** Timeline horizontal zoom — pixels per millisecond. Higher = more zoomed in. */
  timelineZoom: number;
  /** Frames per second used for the timeline ruler grid + quantize snapping. */
  timelineFps: number;
  /** When ON, layer/keyframe drags snap to the nearest frame boundary. */
  timelineQuantize: boolean;
  /** Set to true while a persist write is in flight (drives the topbar spinner). */
  saveBusy: boolean;
  /** Currently focused audio track (for inspector panel). Setting this clears
      layer selection so the inspector shows only one panel at a time. */
  selectedAudioId: string | null;
  /** How canvas mousedown picks a target.
   *   - 'auto'   = click a layer = select it (Photoshop default).
   *   - 'sticky' = mousedown anywhere on canvas drags the currently-selected
   *                layer, regardless of which layer is under the cursor. */
  selectMode: 'auto' | 'sticky';
  /** Currently focused canvas guide for inspector editing. Setting this
   *  clears layer / audio selection. */
  selectedGuideId: string | null;
}

const listeners = new Set<Listener>();
let saveTimer: number | null = null;

function uid(prefix = 'l'): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createDefaultProject(): Project {
  // ─── Children's Day demo as the default project ───
  const now = Date.now();
  return {
    id: uid('p'),
    version: 1,
    name: '5월 5일 ADHD 독립기념일 (데모)',
    createdAt: now,
    updatedAt: now,
    canvas: {
      ratio: '9:16',
      safeRatio: '1:1',
      background: {
        kind: 'gradient',
        from: '#fff8ee',
        to: '#ffe1c4',
        angle: 180,
      },
    },
    duration: 7500,
    layers: [
      {
        id: uid(),
        kind: 'text',
        name: '5월 5일',
        visible: true,
        start: 0,
        end: 7500,
        transform: { x: 540, y: 180, w: 600, h: 100, rotate: 0, opacity: 1 },
        enter: { type: 'pop', duration: 600 },
        text: '5월 5일',
        fontFamily: 'Pretendard',
        fontWeight: 900,
        fontSize: 96,
        color: '#1a1a1a',
        letterSpacing: -3,
        align: 'center',
        lineHeight: 1.1,
      },
      {
        id: uid(),
        kind: 'text',
        name: '어린이날',
        visible: true,
        start: 600,
        end: 2500,
        transform: { x: 540, y: 960, w: 700, h: 180, rotate: 0, opacity: 1 },
            exit: { type: 'fade', duration: 400 },
        text: '어린이날',
        fontFamily: 'Pretendard',
        fontWeight: 900,
        fontSize: 160,
        color: '#1a1a1a',
        letterSpacing: -6,
        align: 'center',
        lineHeight: 1.1,
      },
      {
        id: uid(),
        kind: 'text',
        name: '취소선',
        visible: true,
        start: 1400,
        end: 2500,
        transform: { x: 540, y: 960, w: 750, h: 14, rotate: -3, opacity: 1 },
        enter: { type: 'strike', duration: 550 },
        exit: { type: 'fade', duration: 400 },
        text: '',
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: 14,
        color: '#ff5a5f',
        letterSpacing: 0,
        align: 'left',
        lineHeight: 1,
      },
      {
        id: uid(),
        kind: 'text',
        name: 'ADHD',
        visible: true,
        start: 2500,
        end: 7000,
        transform: { x: 540, y: 960, w: 800, h: 200, rotate: 0, opacity: 1 },
        enter: { type: 'pop', duration: 500 },
        exit: { type: 'fade', duration: 500 },
        text: 'ADHD의 독립기념일로\n나 만들겠어!',
        fontFamily: 'Pretendard',
        fontWeight: 900,
        fontSize: 84,
        color: '#1a1a1a',
        letterSpacing: -3,
        align: 'center',
        lineHeight: 1.15,
      },
      {
        id: uid(),
        kind: 'text',
        name: 'COMING SOON',
        visible: true,
        start: 7000,
        end: 7500,
        transform: { x: 540, y: 960, w: 900, h: 140, rotate: 0, opacity: 1 },
        enter: { type: 'pop', duration: 500 },
        text: 'COMING SOON',
        fontFamily: 'Pretendard',
        fontWeight: 900,
        fontSize: 110,
        color: '#1a1a1a',
        letterSpacing: 4,
        align: 'center',
        lineHeight: 1.1,
      },
    ],
  };
}

// Synchronous "first paint" state — replaced by the IDB-loaded project once init completes.
const state: State = {
  project: createDefaultProject(),
  selectedId: null,
  selectedIds: [],
  editingId: null,
  playhead: 0,
  isPlaying: false,
  recordMode: false,
  tool: 'select',
  zoom: 1,
  displayScale: 1,
  penColor: '#ff5a5f',
  penWidth: 6,
  penMode: 'pen',
  penShape: 'round',
  autoKeyframe: false,
  timelineZoom: 0.12,
  timelineFps: 30,
  timelineQuantize: false,
  saveBusy: false,
  selectedAudioId: null,
  selectMode: 'auto',
  selectedGuideId: null,
};

export function setSelectedGuideId(id: string | null): void {
  state.selectedGuideId = id;
  if (id) {
    state.selectedId = null;
    state.selectedIds = [];
    state.editingId = null;
    state.selectedAudioId = null;
  }
  emit();
}

export function setSelectMode(mode: 'auto' | 'sticky'): void {
  state.selectMode = mode;
  emit();
}

export function setSelectedAudioId(id: string | null): void {
  state.selectedAudioId = id;
  if (id) {
    state.selectedId = null;
    state.selectedIds = [];
    state.editingId = null;
    state.selectedGuideId = null;
  }
  emit();
}

let initPromise: Promise<void> | null = null;

// ─── Undo / redo ───
// Cap undo depth to keep memory bounded — large drag sessions used to
// pile up entries that ran the editor into multi-MB project clones.
const HISTORY_LIMIT = 30;
const HISTORY_DEBOUNCE = 350;
const undoStack: HistoryEntry[] = [];
const redoStack: HistoryEntry[] = [];
let snapshotTimer: number | null = null;

/** Snapshot the current project state for undo. Debounced so a stream of
    drag/typing events collapses into one history entry. */
function pushHistory(label: string): void {
  if (snapshotTimer) {
    window.clearTimeout(snapshotTimer);
  }
  const before = JSON.parse(JSON.stringify(state.project)) as Project;
  const head = undoStack[undoStack.length - 1];
  if (!head || JSON.stringify(head.project) !== JSON.stringify(before)) {
    undoStack.push({ project: before, label, t: Date.now() });
    if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
    redoStack.length = 0;
  } else {
    // Same state — refresh the head's label/timestamp instead of pushing dup.
    head.label = label;
    head.t = Date.now();
  }
  snapshotTimer = window.setTimeout(() => {
    snapshotTimer = null;
  }, HISTORY_DEBOUNCE);
}

export function undo(): void {
  if (snapshotTimer) {
    window.clearTimeout(snapshotTimer);
    snapshotTimer = null;
  }
  const prev = undoStack.pop();
  if (!prev) return;
  redoStack.push({
    project: JSON.parse(JSON.stringify(state.project)),
    label: prev.label,
    t: Date.now(),
  });
  state.project = prev.project;
  state.selectedId = null;
  state.selectedIds = [];
  state.editingId = null;
  emit();
  void dbSet(PROJECT_PREFIX + state.project.id, state.project);
  persistHistoryNow();
}

export function redo(): void {
  const next = redoStack.pop();
  if (!next) return;
  undoStack.push({
    project: JSON.parse(JSON.stringify(state.project)),
    label: next.label,
    t: Date.now(),
  });
  state.project = next.project;
  state.selectedId = null;
  state.selectedIds = [];
  state.editingId = null;
  emit();
  void dbSet(PROJECT_PREFIX + state.project.id, state.project);
  persistHistoryNow();
}

export function canUndo(): boolean {
  return undoStack.length > 0;
}
export function canRedo(): boolean {
  return redoStack.length > 0;
}

/** How many undo / redo entries are currently stacked — drives the UI
 *  badge so the user can see whether a history-clear is worth doing. */
export function historyDepth(): { undo: number; redo: number } {
  return { undo: undoStack.length, redo: redoStack.length };
}

/** Read-only snapshot of the history trail for the popover UI. The head
 *  of `undo` is the most recent change; redo is in pop order (last is
 *  the next thing redo() would apply). */
export function historyList(): { undo: HistoryEntry[]; redo: HistoryEntry[] } {
  return {
    undo: undoStack.map((e) => ({ project: e.project, label: e.label, t: e.t })),
    redo: redoStack.map((e) => ({ project: e.project, label: e.label, t: e.t })),
  };
}

/** Wipe the undo / redo stacks. Use this when a long drag session has
 *  bloated history and the editor starts feeling sluggish — the current
 *  project state is preserved, only the trail is dropped. */
export function clearHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
  if (snapshotTimer) {
    window.clearTimeout(snapshotTimer);
    snapshotTimer = null;
  }
  persistHistoryNow();
  emit();
}

/** Run once on app start: migrate any legacy localStorage payload, then load the current project from IDB. */
export function initStore(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      await migrateLegacyLocalStorage();
      // One-time sweep: history records used to balloon (full-project clones
      // x 30 entries). Now that history is session-only, garbage-collect
      // every history:* key sitting on disk so loads aren't dragged down.
      try {
        const keys = await dbKeys();
        for (const k of keys) {
          if (typeof k === 'string' && k.startsWith(HISTORY_PREFIX)) {
            await dbDelete(k);
          }
        }
      } catch {
        /* ignore */
      }
      const currentId = await dbGet<string>(META_KEY_CURRENT);
      if (currentId) {
        const project = await dbGet<Project>(PROJECT_PREFIX + currentId);
        if (project && isValidProject(project)) {
          state.project = ensureProjectShape(migrateProject(project));
          await loadHistoryFor(currentId);
          emit();
          return;
        }
      }
      // No current project — keep the default already in state and persist it.
      await dbSet(PROJECT_PREFIX + state.project.id, state.project);
      await dbSet(META_KEY_CURRENT, state.project.id);
      emit();
    } catch (err) {
      console.warn('Store init failed; using in-memory default', err);
    }
  })();
  return initPromise;
}

async function loadHistoryFor(projectId: string): Promise<void> {
  // Session-only history (see persistHistoryNow). Always start empty AND
  // sweep any leftover IDB record so old projects don't keep dragging a
  // bloated history blob around.
  undoStack.length = 0;
  redoStack.length = 0;
  try {
    await dbDelete(HISTORY_PREFIX + projectId);
  } catch {
    /* ignore */
  }
}

async function migrateLegacyLocalStorage(): Promise<void> {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<Project>;
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.layers)) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return;
    }
    const reshaped = ensureProjectShape(migrateProject(parsed as Project));
    await dbSet(PROJECT_PREFIX + reshaped.id, reshaped);
    await dbSet(META_KEY_CURRENT, reshaped.id);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (err) {
    console.warn('Legacy migration failed', err);
  }
}

/** Patches old projects in-place. Safe / idempotent / additive only. */
function migrateProject(p: Project): Project {
  for (const l of p.layers) {
    if (l.kind === 'text' && l.name === '취소선' && l.enter?.type === 'slide') {
      l.enter = { type: 'strike', duration: 550 };
    }
  }
  migrateAudioField(p);
  migrateScripts(p);
  return p;
}

/** Older projects predate id/createdAt/updatedAt; back-fill before saving. */
function ensureProjectShape(p: Project): Project {
  const now = Date.now();
  if (!p.id) p.id = uid('p');
  if (!p.createdAt) p.createdAt = now;
  if (!p.updatedAt) p.updatedAt = now;
  return p;
}

function isValidProject(p: unknown): p is Project {
  const obj = p as Project | null;
  return !!obj && obj.version === 1 && Array.isArray(obj.layers) && !!obj.canvas;
}

/** Show the saving spinner for at least this long so the local flush is
    actually visible to the user. Kept short — the goal is honest feedback,
    not visual noise that triggers extra renders. */
const SAVE_SPINNER_MIN_MS = 250;
let spinnerHideTimer: number | null = null;

function persist(): void {
  if (saveTimer) clearTimeout(saveTimer);
  // No emit here. updateProject() already emitted once with the new state;
  // firing emit() again just to set saveBusy=true would double the render
  // cost on every state change (canvas/timeline/inspector all re-render
  // again). The spinner can lag the actual change by a frame — that's fine
  // because the visible work is the project change, not the spinner.
  state.saveBusy = true;
  if (spinnerHideTimer) clearTimeout(spinnerHideTimer);
  saveTimer = window.setTimeout(() => {
    state.project.updatedAt = Date.now();
    const id = state.project.id;
    dbSet(PROJECT_PREFIX + id, state.project)
      .catch((err) => {
        console.warn('Autosave failed', err);
      })
      .finally(() => {
        spinnerHideTimer = window.setTimeout(() => {
          state.saveBusy = false;
          spinnerHideTimer = null;
          emit(); // single emit when save genuinely finishes
        }, SAVE_SPINNER_MIN_MS);
      });
  }, SAVE_DEBOUNCE);
}

/** History is now session-scoped — we no longer write the undo/redo trail
 *  to IDB. Each entry is a full Project clone, so 30 entries × multi-MB
 *  projects (audio + image data URLs) made every save grind. The trade-off:
 *  a reload starts the trail empty, which matches how Photoshop / Figma
 *  treat their session history. */
function persistHistoryNow(): void {
  /* no-op — kept as an export shim for older callers */
}

function emit(): void {
  for (const fn of listeners) fn(state);
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function getState(): Readonly<State> {
  return state;
}

// ─── Mutations (always emit + persist project changes) ───

export function setProject(p: Project): void {
  pushHistory(`${p.name || '프로젝트'} · 불러오기`);
  state.project = p;
  state.selectedId = null;
  state.selectedIds = [];
  state.playhead = 0;
  emit();
  persist();
}

export function updateProject(updater: (p: Project) => Project, label = '수정'): void {
  pushHistory(label);
  state.project = updater(state.project);
  emit();
  persist();
}

export function setSelectedId(id: string | null): void {
  state.selectedId = id;
  state.selectedIds = id ? [id] : [];
  if (id !== state.editingId) state.editingId = null;
  if (id) {
    state.selectedAudioId = null;
    state.selectedGuideId = null;
  }
  emit();
}

export function setSelectedIds(ids: string[]): void {
  // Dedupe; last entry becomes the legacy `selectedId`.
  const dedup: string[] = [];
  for (const id of ids) {
    if (!dedup.includes(id)) dedup.push(id);
  }
  state.selectedIds = dedup;
  state.selectedId = dedup.length > 0 ? (dedup[dedup.length - 1] as string) : null;
  if (state.selectedId !== state.editingId) state.editingId = null;
  emit();
}

export function setEditingId(id: string | null): void {
  state.editingId = id;
  if (id) {
    state.selectedId = id;
    if (!state.selectedIds.includes(id)) state.selectedIds = [id];
  }
  emit();
}

export function setPlayhead(ms: number): void {
  const clamped = Math.max(0, Math.min(state.project.duration, ms));
  state.playhead = clamped;
  emit();
}

export function setPlaying(playing: boolean): void {
  state.isPlaying = playing;
  emit();
}

export function setRecordMode(on: boolean): void {
  state.recordMode = on;
  emit();
}

export function setTool(t: Tool): void {
  state.tool = t;
  emit();
}

export function setZoom(z: number): void {
  state.zoom = Math.max(0.1, Math.min(8, z));
  emit();
}

export function setDisplayScale(s: number): void {
  if (Math.abs(state.displayScale - s) < 0.0005) return;
  state.displayScale = s;
  emit();
}

export function setPenColor(c: string): void {
  state.penColor = c;
  emit();
}

export function setPenWidth(w: number): void {
  state.penWidth = Math.max(1, Math.min(80, w));
  emit();
}

export function setPenMode(m: 'pen' | 'eraser'): void {
  state.penMode = m;
  emit();
}

export function setPenShape(s: 'round' | 'butt'): void {
  state.penShape = s;
  emit();
}

export function setAutoKeyframe(on: boolean): void {
  state.autoKeyframe = on;
  emit();
}

export function setTimelineZoom(z: number): void {
  state.timelineZoom = Math.max(0.02, Math.min(1.5, z));
  emit();
}
export function setTimelineFps(fps: number): void {
  state.timelineFps = Math.max(1, Math.min(120, Math.round(fps)));
  emit();
}
export function setTimelineQuantize(on: boolean): void {
  state.timelineQuantize = on;
  emit();
}

/** Snap a project-time value to the nearest frame boundary.
 *  - If `force` is true, snap regardless of the quantize toggle (used by
 *    Shift-modified drags so the user can opt into snapping ad hoc).
 *  - Otherwise honour state.timelineQuantize. */
export function quantizeTime(t: number, force = false): number {
  if (!force && !state.timelineQuantize) return t;
  const frameMs = 1000 / state.timelineFps;
  return Math.round(t / frameMs) * frameMs;
}

// ─── Keyframes ───
export function addKeyframe(layerId: string, kf: Keyframe): void {
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) =>
      l.id === layerId ? ({ ...l, keyframes: upsertKeyframe(l.keyframes, kf) } as Layer) : l,
    ),
  }));
}

/** Snapshot the layer's current inspector-editable values into a keyframe at t. */
export function snapshotKeyframe(layerId: string, t: number, ease: Easing = 'linear'): void {
  const layer = state.project.layers.find((l) => l.id === layerId);
  if (!layer) return;
  const values = pickInspectorValues(layer);
  addKeyframe(layerId, {
    id: uid('k'),
    t,
    values,
    ease,
  });
}

/** Update or create a keyframe at the playhead with a single value path
    (e.g. when the user changes the inspector with auto-keyframe ON). */
export function recordKeyframeValue(
  layerId: string,
  t: number,
  path: string,
  value: unknown,
  ease: Easing = 'linear',
): void {
  const partial: Record<string, unknown> = {};
  setNestedPath(partial, path, value);
  addKeyframe(layerId, { id: uid('k'), t, values: partial, ease });
}

export function updateKeyframe(layerId: string, kfId: string, patch: Partial<Keyframe>): void {
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) => {
      if (l.id !== layerId) return l;
      const next = (l.keyframes ?? [])
        .map((k) => (k.id === kfId ? { ...k, ...patch } : k))
        .sort((a, b) => a.t - b.t);
      return { ...l, keyframes: next } as Layer;
    }),
  }));
}

export function removeKeyframe(layerId: string, kfId: string): void {
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) => {
      if (l.id !== layerId) return l;
      const next = (l.keyframes ?? []).filter((k) => k.id !== kfId);
      return { ...l, keyframes: next } as Layer;
    }),
  }));
}

/** Inspector-editable fields per layer kind. Shape kept narrow so accidental
    fields (e.g. id) don't leak into keyframe values. */
function pickInspectorValues(layer: Layer): Record<string, unknown> {
  const t = layer.transform;
  const values: Record<string, unknown> = {
    transform: { x: t.x, y: t.y, w: t.w, h: t.h, rotate: t.rotate, opacity: t.opacity },
  };
  switch (layer.kind) {
    case 'text':
      values.text = layer.text;
      values.fontFamily = layer.fontFamily;
      values.fontWeight = layer.fontWeight;
      values.fontSize = layer.fontSize;
      values.color = layer.color;
      values.letterSpacing = layer.letterSpacing;
      values.lineHeight = layer.lineHeight;
      values.align = layer.align;
      break;
    case 'image':
      values.fit = layer.fit;
      break;
    case 'video':
      values.fit = layer.fit;
      values.muted = layer.muted;
      values.loop = layer.loop;
      break;
    case 'shape':
      values.shape = layer.shape;
      values.fill = layer.fill;
      values.stroke = layer.stroke;
      values.strokeWidth = layer.strokeWidth;
      break;
    case 'drawing':
      // brush settings — stroke list itself is intentionally NOT keyframed
      if (layer.brush) values.brush = { ...layer.brush };
      break;
  }
  return values;
}

function setNestedPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i] as string;
    const n = cur[p];
    if (typeof n !== 'object' || n === null) cur[p] = {};
    cur = cur[p] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1] as string] = value;
}

/** Append a stroke to a drawing layer (creates layer if needed). */
export function addStroke(
  layerId: string | null,
  stroke: import('../types').DrawingStroke,
): string {
  let id = layerId;
  if (!id) {
    const layer = makeDrawingLayer();
    addLayer(layer);
    id = layer.id;
  }
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) => {
      if (l.id !== id) return l;
      if (l.kind !== 'drawing') return l;
      return { ...l, strokes: [...l.strokes, stroke] };
    }),
  }));
  return id;
}

/** Live-extend the last stroke on a drawing layer with new points (mutates in place to keep drag fast). */
export function appendStrokePoint(layerId: string, x: number, y: number): void {
  const layer = state.project.layers.find((l) => l.id === layerId);
  if (!layer || layer.kind !== 'drawing') return;
  const last = layer.strokes[layer.strokes.length - 1];
  if (!last) return;
  last.points.push(x, y);
  emit();
  persist();
}

export function addLayer(layer: Layer): void {
  updateProject((p) => ({ ...p, layers: [...p.layers, layer] }), `${layer.name} · 추가`);
  state.selectedId = layer.id;
  state.selectedIds = [layer.id];
  emit();
}

export function removeLayer(id: string): void {
  const lname = state.project.layers.find((l) => l.id === id)?.name ?? '레이어';
  updateProject((p) => ({ ...p, layers: p.layers.filter((l) => l.id !== id) }), `${lname} · 삭제`);
  state.selectedIds = state.selectedIds.filter((x) => x !== id);
  if (state.selectedId === id) {
    state.selectedId = state.selectedIds[state.selectedIds.length - 1] ?? null;
  }
  emit();
}

// ─── Layer groups ─────────────────────────────────────────────────────
export function createGroupFromSelection(): string | null {
  const ids = state.selectedIds.length > 0 ? [...state.selectedIds] : (state.selectedId ? [state.selectedId] : []);
  if (ids.length < 1) return null;
  const groupId = uid('g');
  const group: LayerGroup = { id: groupId, name: '새 그룹', collapsed: false };
  updateProject((p) => ({
    ...p,
    groups: [...(p.groups ?? []), group],
    layers: p.layers.map((l) => (ids.includes(l.id) ? ({ ...l, groupId } as Layer) : l)),
  }));
  return groupId;
}

/** Create a brand-new empty group. The user then drags layers into it via
 *  the timeline side panel. */
export function createEmptyGroup(name = '새 그룹'): string {
  const groupId = uid('g');
  const group: LayerGroup = { id: groupId, name, collapsed: false };
  updateProject((p) => ({ ...p, groups: [...(p.groups ?? []), group] }), `${name} · 그룹 생성`);
  return groupId;
}

/** Move a layer in or out of a group. Pass `undefined` to ungroup. */
export function setLayerGroup(layerId: string, groupId: string | undefined): void {
  const lname = state.project.layers.find((l) => l.id === layerId)?.name ?? '레이어';
  const gname = groupId ? state.project.groups?.find((g) => g.id === groupId)?.name ?? '그룹' : null;
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) =>
      l.id === layerId ? ({ ...l, groupId } as Layer) : l,
    ),
  }), gname ? `${lname} → ${gname}` : `${lname} · 그룹 해제`);
}

export function ungroup(groupId: string): void {
  const gname = state.project.groups?.find((g) => g.id === groupId)?.name ?? '그룹';
  updateProject((p) => ({
    ...p,
    groups: (p.groups ?? []).filter((g) => g.id !== groupId),
    layers: p.layers.map((l) =>
      l.groupId === groupId ? ({ ...l, groupId: undefined } as Layer) : l,
    ),
  }), `${gname} · 그룹 해제`);
}

export function setGroupCollapsed(groupId: string, collapsed: boolean): void {
  updateProject((p) => ({
    ...p,
    groups: (p.groups ?? []).map((g) => (g.id === groupId ? { ...g, collapsed } : g)),
  }));
}
export function toggleGroupCollapsed(groupId: string): void {
  const g = state.project.groups?.find((x) => x.id === groupId);
  if (g) setGroupCollapsed(groupId, !g.collapsed);
}

export function renameGroup(groupId: string, name: string): void {
  updateProject((p) => ({
    ...p,
    groups: (p.groups ?? []).map((g) => (g.id === groupId ? { ...g, name } : g)),
  }));
}

export function setGroupColor(groupId: string, color: string | undefined): void {
  updateProject((p) => ({
    ...p,
    groups: (p.groups ?? []).map((g) => (g.id === groupId ? { ...g, color } : g)),
  }));
}

/** Remove every layer in `ids` (used by lasso-multi-select Delete). */
export function removeLayers(ids: string[]): void {
  if (ids.length === 0) return;
  const set = new Set(ids);
  updateProject((p) => ({ ...p, layers: p.layers.filter((l) => !set.has(l.id)) }));
  state.selectedIds = state.selectedIds.filter((x) => !set.has(x));
  state.selectedId = state.selectedIds[state.selectedIds.length - 1] ?? null;
  emit();
}

export function updateLayer(id: string, patch: Partial<Layer>): void {
  const lname = state.project.layers.find((l) => l.id === id)?.name ?? '레이어';
  // Pick the first patched key as a hint — "name" / "color" / "text" etc.
  const keys = Object.keys(patch);
  const fieldKo: Record<string, string> = {
    name: '이름', color: '색상', text: '텍스트', fontFamily: '폰트',
    fontSize: '크기', fontWeight: '굵기', align: '정렬', fit: '맞춤',
    muted: '음소거', loop: '반복', shape: '도형', fill: '채움',
    stroke: '선', strokeWidth: '선두께', visible: '표시',
    effects: '이펙트', start: '시간', end: '시간', enter: '입장', exit: '퇴장',
    keyframes: '키프레임', brush: '브러시', groupId: '그룹',
  };
  const field = keys.length === 1 ? (fieldKo[keys[0] as string] ?? keys[0]) : '변경';
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) => (l.id === id ? ({ ...l, ...patch } as Layer) : l)),
  }), `${lname} · ${field}`);
  maybeAutoKeyframe(id, filterKeyframableLayerPatch(patch));
}

export function updateLayerTransform(id: string, patch: Partial<Transform>): void {
  const lname = state.project.layers.find((l) => l.id === id)?.name ?? '레이어';
  const keys = Object.keys(patch);
  const xy = keys.includes('x') || keys.includes('y');
  const wh = keys.includes('w') || keys.includes('h');
  const rot = keys.includes('rotate');
  const op = keys.includes('opacity');
  const what = wh ? '크기' : (rot ? '회전' : (op ? '투명도' : (xy ? '이동' : '변형')));
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) =>
      l.id === id ? { ...l, transform: { ...l.transform, ...patch } } : l,
    ),
  }), `${lname} · ${what}`);
  if (Object.keys(patch).length > 0) {
    maybeAutoKeyframe(id, { transform: { ...patch } });
  }
}

/** Whitelist of `updateLayer` patch keys that participate in keyframes.
    Keep narrow — non-interpolatable / structural fields stay out. */
const KEYFRAMABLE_LAYER_KEYS = new Set<string>([
  'text',
  'fontFamily',
  'fontWeight',
  'fontSize',
  'color',
  'letterSpacing',
  'lineHeight',
  'align',
  'fit',
  'muted',
  'loop',
  'shape',
  'fill',
  'stroke',
  'strokeWidth',
  'brush',
  'transform',
  // CSS effects panel — leaf paths get tweened by computeAt: numeric
  // filters lerp, hex colours lerp, strings step. Lets the user tween
  // blur / brightness / shadow x-y / corner radius / etc. with keyframes.
  'effects',
]);

function filterKeyframableLayerPatch(patch: Partial<Layer>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(patch)) {
    if (KEYFRAMABLE_LAYER_KEYS.has(k)) {
      out[k] = (patch as Record<string, unknown>)[k];
    }
  }
  return out;
}

function maybeAutoKeyframe(layerId: string, values: Record<string, unknown>): void {
  if (!state.autoKeyframe) return;
  if (Object.keys(values).length === 0) return;
  const layer = state.project.layers.find((l) => l.id === layerId);
  if (!layer) return;
  addKeyframe(layerId, {
    id: uid('k'),
    t: state.playhead,
    values,
    ease: 'linear',
  });
}

export function reorderLayer(id: string, toIndex: number): void {
  updateProject((p) => {
    const idx = p.layers.findIndex((l) => l.id === id);
    if (idx < 0) return p;
    const next = [...p.layers];
    const [item] = next.splice(idx, 1);
    if (item) next.splice(Math.max(0, Math.min(next.length, toIndex)), 0, item);
    return { ...p, layers: next };
  });
}

export function setAudio(audio: AudioTrack | undefined): void {
  updateProject((p) => ({
    ...p,
    audioTracks: audio ? [audio] : [],
    // legacy single field kept clean
    audio: undefined,
  }));
}

export function addAudioTrack(track: AudioTrack): void {
  const tname = track.label || track.kind;
  updateProject((p) => ({
    ...p,
    audioTracks: [...(p.audioTracks ?? []), track],
    audio: undefined,
  }), `${tname} · 사운드 추가`);
}

export function updateAudioTrack(id: string, patch: Partial<AudioTrack>): void {
  const t = state.project.audioTracks?.find((a) => a.id === id);
  const tname = t?.label || t?.kind || '사운드';
  const keys = Object.keys(patch);
  const what = keys.includes('songStart') || keys.includes('songEnd') ? '범위'
    : keys.includes('volume') ? '볼륨'
    : keys.includes('label') ? '이름'
    : '변경';
  updateProject((p) => ({
    ...p,
    audioTracks: (p.audioTracks ?? []).map((t) =>
      t.id === id ? ({ ...t, ...patch } as AudioTrack) : t,
    ),
  }), `${tname} · ${what}`);
}

export function removeAudioTrack(id: string): void {
  const tname = state.project.audioTracks?.find((a) => a.id === id)?.label || '사운드';
  updateProject((p) => ({
    ...p,
    audioTracks: (p.audioTracks ?? []).filter((t) => t.id !== id),
  }), `${tname} · 삭제`);
}

// ─── Guides ──────────────────────────────────────────────────────────────
export function addGuide(orientation: 'vertical' | 'horizontal'): void {
  updateProject((p) => {
    const dims = p.canvas.customSize ?? RATIO_DIMS[p.canvas.ratio];
    const center = orientation === 'vertical' ? dims.w / 2 : dims.h / 2;
    return {
      ...p,
      guides: [...(p.guides ?? []), { id: uid('gd'), orientation, position: center }],
    };
  }, `가이드 · ${orientation === 'vertical' ? '세로' : '가로'} 추가`);
}

export function updateGuide(
  id: string,
  patch: Partial<{ position: number; width: number; color: string; visible: boolean }>,
): void {
  const keys = Object.keys(patch);
  const what = keys.includes('position') ? '이동'
    : keys.includes('width') ? '두께'
    : keys.includes('color') ? '색상'
    : keys.includes('visible') ? '표시'
    : '변경';
  updateProject((p) => ({
    ...p,
    guides: (p.guides ?? []).map((g) => (g.id === id ? { ...g, ...patch } : g)),
  }), `가이드 · ${what}`);
}

export function removeGuide(id: string): void {
  updateProject((p) => ({
    ...p,
    guides: (p.guides ?? []).filter((g) => g.id !== id),
  }), '가이드 · 삭제');
}

export function clearGuides(): void {
  updateProject((p) => ({ ...p, guides: [] }));
}

/** Migration helper — resolve `project.audio` (legacy) into `audioTracks`. */
function migrateAudioField(p: Project): void {
  if (p.audio && (!p.audioTracks || p.audioTracks.length === 0)) {
    const t = p.audio;
    if (!t.id) (t as AudioTrack & { id: string }).id = uid('a');
    p.audioTracks = [t];
    delete p.audio;
  }
}

/** Migrate legacy `globalCss` / `globalScript` into `scripts[]` chunks. */
function migrateScripts(p: Project): void {
  if (p.scripts && p.scripts.length > 0) return;
  const chunks: ScriptChunk[] = [];
  if (p.globalCss && p.globalCss.trim()) {
    chunks.push({
      id: uid('s'),
      name: 'CSS',
      lang: 'css',
      enabled: true,
      source: p.globalCss,
    });
  }
  if (p.globalScript && p.globalScript.trim()) {
    chunks.push({
      id: uid('s'),
      name: 'JS',
      lang: 'js',
      enabled: true,
      source: p.globalScript,
    });
  }
  if (chunks.length > 0) p.scripts = chunks;
}

/** Concatenate enabled chunks of a given lang. */
export function concatScripts(p: Project, lang: 'css' | 'js'): string {
  const chunks = p.scripts ?? [];
  const enabled = chunks.filter((c) => c.enabled !== false && c.lang === lang);
  if (lang === 'css') return enabled.map((c) => c.source).join('\n\n');
  // JS: each chunk wrapped in IIFE so a `return` or `throw` doesn't kill siblings.
  return enabled
    .map(
      (c) =>
        `try { (function(){\n${c.source}\n}).call(this); } catch (err) { console.warn('script chunk ${c.name.replace(/[`$\\]/g, '')}', err); }`,
    )
    .join('\n');
}

export function addScriptChunk(lang: 'css' | 'js'): ScriptChunk {
  const chunk: ScriptChunk = {
    id: uid('s'),
    name: lang === 'css' ? '새 CSS' : '새 JS',
    lang,
    enabled: true,
    source: '',
  };
  updateProject((p) => ({ ...p, scripts: [...(p.scripts ?? []), chunk] }));
  return chunk;
}

export function updateScriptChunk(id: string, patch: Partial<ScriptChunk>): void {
  updateProject((p) => ({
    ...p,
    scripts: (p.scripts ?? []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
  }));
}

export function removeScriptChunk(id: string): void {
  updateProject((p) => ({
    ...p,
    scripts: (p.scripts ?? []).filter((c) => c.id !== id),
  }));
}

export function duplicateLayer(id: string): void {
  const original = state.project.layers.find((l) => l.id === id);
  if (!original) return;
  const copy = { ...original, id: uid(), name: `${original.name} 사본` } as Layer;
  addLayer(copy);
}

// ─── Z-order helpers (front = end of array) ───
export function bringToFront(id: string): void {
  const layers = state.project.layers;
  const idx = layers.findIndex((l) => l.id === id);
  if (idx < 0 || idx === layers.length - 1) return;
  reorderLayer(id, layers.length - 1);
}
export function sendToBack(id: string): void {
  const layers = state.project.layers;
  const idx = layers.findIndex((l) => l.id === id);
  if (idx <= 0) return;
  reorderLayer(id, 0);
}
export function bringForward(id: string): void {
  const layers = state.project.layers;
  const idx = layers.findIndex((l) => l.id === id);
  if (idx < 0 || idx === layers.length - 1) return;
  reorderLayer(id, idx + 1);
}
export function sendBackward(id: string): void {
  const layers = state.project.layers;
  const idx = layers.findIndex((l) => l.id === id);
  if (idx <= 0) return;
  reorderLayer(id, idx - 1);
}

export function toggleLayerVisible(id: string): void {
  const l = state.project.layers.find((x) => x.id === id);
  if (!l) return;
  updateLayer(id, { visible: !l.visible });
}

export function centerLayerH(id: string): void {
  const l = state.project.layers.find((x) => x.id === id);
  if (!l) return;
  const cw = canvasWidth();
  updateLayerTransform(id, { x: Math.round((cw - l.transform.w) / 2) });
}
export function centerLayerV(id: string): void {
  const l = state.project.layers.find((x) => x.id === id);
  if (!l) return;
  const ch = canvasHeight();
  updateLayerTransform(id, { y: Math.round((ch - l.transform.h) / 2) });
}

function canvasWidth(): number {
  const c = state.project.canvas;
  if (c.customSize) return c.customSize.w;
  return RATIO_DIMS[c.ratio].w;
}
function canvasHeight(): number {
  const c = state.project.canvas;
  if (c.customSize) return c.customSize.h;
  return RATIO_DIMS[c.ratio].h;
}

export function removeAllKeyframes(id: string): void {
  updateProject((p) => ({
    ...p,
    layers: p.layers.map((l) => (l.id === id ? ({ ...l, keyframes: [] } as Layer) : l)),
  }));
}

export function setLayerStartToPlayhead(id: string): void {
  const l = state.project.layers.find((x) => x.id === id);
  if (!l) return;
  const t = state.playhead;
  if (t >= l.end) return;
  updateLayer(id, { start: t });
}
export function setLayerEndToPlayhead(id: string): void {
  const l = state.project.layers.find((x) => x.id === id);
  if (!l) return;
  const t = state.playhead;
  if (t <= l.start) return;
  updateLayer(id, { end: t });
}

// ─── Clipboard (session-only) ───
let clipboardLayer: Layer | null = null;
export function copyLayer(id: string): void {
  const l = state.project.layers.find((x) => x.id === id);
  if (!l) return;
  clipboardLayer = JSON.parse(JSON.stringify(l)) as Layer;
}
export function cutLayer(id: string): void {
  copyLayer(id);
  removeLayer(id);
}
export function pasteLayer(): void {
  if (!clipboardLayer) return;
  const copy = {
    ...clipboardLayer,
    id: uid(),
    name: `${clipboardLayer.name} 사본`,
    transform: {
      ...clipboardLayer.transform,
      x: clipboardLayer.transform.x + 20,
      y: clipboardLayer.transform.y + 20,
    },
  } as Layer;
  addLayer(copy);
}
export function hasClipboard(): boolean {
  return clipboardLayer !== null;
}

// ─── Factories ───

export function makeTextLayer(text = '새 텍스트'): Layer {
  return {
    id: uid(),
    kind: 'text',
    name: text.slice(0, 20) || '텍스트',
    visible: true,
    start: 0,
    end: state.project.duration,
    transform: { x: 540, y: 540, w: 600, h: 120, rotate: 0, opacity: 1 },
    text,
    fontFamily: 'Pretendard',
    fontWeight: 800,
    fontSize: 72,
    color: '#1a1a1a',
    letterSpacing: -2,
    align: 'center',
    lineHeight: 1.2,
  };
}

export function makeImageLayer(src: string, name = '이미지'): Layer {
  return {
    id: uid(),
    kind: 'image',
    name,
    visible: true,
    start: 0,
    end: state.project.duration,
    transform: { x: 540, y: 540, w: 600, h: 600, rotate: 0, opacity: 1 },
    src,
    fit: 'cover',
  };
}

export function makeVideoLayer(src: string, name = '동영상'): Layer {
  return {
    id: uid(),
    kind: 'video',
    name,
    visible: true,
    start: 0,
    end: state.project.duration,
    transform: { x: 540, y: 540, w: 800, h: 600, rotate: 0, opacity: 1 },
    src,
    fit: 'cover',
    muted: true,
    loop: true,
  };
}

export function makeDrawingLayer(): Layer {
  return {
    id: uid(),
    kind: 'drawing',
    name: '그림',
    visible: true,
    start: 0,
    end: state.project.duration,
    transform: { x: 540, y: 540, w: 1080, h: 1080, rotate: 0, opacity: 1 },
    strokes: [],
  };
}

export function makeShapeLayer(): Layer {
  return {
    id: uid(),
    kind: 'shape',
    name: '도형',
    visible: true,
    start: 0,
    end: state.project.duration,
    transform: { x: 540, y: 540, w: 400, h: 400, rotate: 0, opacity: 1 },
    shape: 'box',
    fill: '#ff5a5f',
    stroke: 'transparent',
    strokeWidth: 0,
  };
}

// ─── Multi-project API (IDB-backed) ───

export async function listProjects(): Promise<ProjectMeta[]> {
  const keys = await dbKeys(PROJECT_PREFIX);
  const metas: ProjectMeta[] = [];
  for (const k of keys) {
    const p = await dbGet<Project>(k);
    if (p && isValidProject(p)) {
      metas.push({
        id: p.id,
        name: p.name,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      });
    }
  }
  metas.sort((a, b) => b.updatedAt - a.updatedAt);
  return metas;
}

export async function switchProject(id: string): Promise<void> {
  const p = await dbGet<Project>(PROJECT_PREFIX + id);
  if (!p || !isValidProject(p)) throw new Error(`Project not found: ${id}`);
  // Flush the outgoing project's history so it's there next time we visit it.
  persistHistoryNow();
  state.project = ensureProjectShape(migrateProject(p));
  state.selectedId = null;
  state.selectedIds = [];
  state.editingId = null;
  state.playhead = 0;
  state.isPlaying = false;
  await dbSet(META_KEY_CURRENT, id);
  await loadHistoryFor(id);
  emit();
}

export async function createProject(name = '새 프로젝트'): Promise<Project> {
  const p = createDefaultProject();
  p.id = uid('p');
  p.name = name;
  p.createdAt = Date.now();
  p.updatedAt = p.createdAt;
  // Start blank instead of demo-laden
  p.layers = [];
  await dbSet(PROJECT_PREFIX + p.id, p);
  await switchProject(p.id);
  return p;
}

export async function duplicateProject(id: string): Promise<Project | null> {
  const src = await dbGet<Project>(PROJECT_PREFIX + id);
  if (!src) return null;
  const copy: Project = {
    ...src,
    id: uid('p'),
    name: `${src.name} 사본`,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    layers: src.layers.map((l) => ({ ...l, id: uid() })),
  };
  await dbSet(PROJECT_PREFIX + copy.id, copy);
  return copy;
}

export async function renameProject(id: string, name: string): Promise<void> {
  const p = await dbGet<Project>(PROJECT_PREFIX + id);
  if (!p) return;
  p.name = name;
  p.updatedAt = Date.now();
  await dbSet(PROJECT_PREFIX + id, p);
  if (state.project.id === id) {
    state.project = p;
    emit();
  }
}

export async function deleteProject(id: string): Promise<void> {
  await dbDelete(PROJECT_PREFIX + id);
  if (state.project.id === id) {
    const remaining = await listProjects();
    if (remaining.length > 0) {
      await switchProject(remaining[0]!.id);
    } else {
      const fresh = createDefaultProject();
      await dbSet(PROJECT_PREFIX + fresh.id, fresh);
      await switchProject(fresh.id);
    }
  }
}

// ─── Helpers exposed to UI ───
export { uid };
